import { uniq } from 'lodash-es';

import type { TableSelectionType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { IRobloxTopChartsRow } from '../types';

export interface IRobloxTopChartsChartSeriesItem {
  id: string;
  name: string;
  cover: string;
  color?: string;
  history: Array<{
    metric: string;
    source: string;
    time: string;
    value: number;
  }>;
}

const momentCompare = (left: string, right: string): number => {
  if (left === right) {
    return 0;
  }

  return left > right ? 1 : -1;
};

const getMetricValue = (row: IRobloxTopChartsRow, chartMetric: string): number => {
  const value = Number(row?.[chartMetric]);
  return Number.isFinite(value) ? value : Number.NaN;
};

const aggregateRowsByGameAndDate = (
  list: IRobloxTopChartsRow[],
  chartMetric: string,
): Map<string, number> => {
  const valueMap = new Map<string, number>();

  list.forEach((row) => {
    const gameId = String(row?.game_id || '');
    const date = String(row?.date || '');
    if (!gameId || !date) {
      return;
    }

    const key = `${gameId}__${date}`;
    const metricValue = getMetricValue(row, chartMetric);
    if (!Number.isFinite(metricValue)) {
      return;
    }

    const currentValue = valueMap.get(key);
    if (currentValue === undefined) {
      valueMap.set(key, metricValue);
      return;
    }

    valueMap.set(key, Math.max(currentValue, metricValue));
  });

  return valueMap;
};

export const getRobloxTopChartsChartXAxisDates = (
  list: IRobloxTopChartsRow[],
): string[] => {
  const dates = list
    .map((item) => String(item?.date || ''))
    .filter(Boolean);

  return uniq(dates).sort(momentCompare);
};

export const transformRobloxTopChartsChartLineSeries = (
  list: IRobloxTopChartsRow[],
  tableSelection: TableSelectionType[],
  chartMetric: string,
): IRobloxTopChartsChartSeriesItem[] => {
  const selectedIds = tableSelection
    .map((item) => String(item?.gameId || (item as IRobloxTopChartsRow)?.game_id || ''))
    .filter(Boolean);

  if (!selectedIds.length || !chartMetric) {
    return [];
  }

  const aggregatedValueMap = aggregateRowsByGameAndDate(list, chartMetric);

  return selectedIds.map((gameId) => {
    const selectionItem = tableSelection.find(
      (item) => String(item?.gameId || (item as IRobloxTopChartsRow)?.game_id) === gameId,
    );
    const matchedRow = list.find((row) => String(row?.game_id || '') === gameId);

    const history = Array.from(aggregatedValueMap.entries())
      .filter(([key]) => key.startsWith(`${gameId}__`))
      .map(([key, value]) => ({
        metric: chartMetric,
        source: '',
        time: key.replace(`${gameId}__`, ''),
        value,
      }))
      .sort((left, right) => momentCompare(left.time, right.time));

    return {
      id: gameId,
      name: String(
        matchedRow?.entity_name
        || selectionItem?.gametitle
        || selectionItem?.entity_name
        || gameId,
      ),
      cover: String(matchedRow?.cover || selectionItem?.img || selectionItem?.cover || ''),
      color: selectionItem?.color,
      history,
    };
  });
};
